import { useEffect, useId, useState } from "react";
import PropTypes from "prop-types";
import { NavLink, useLocation } from "react-router-dom";
import {
  Collapse,
  Icon,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { useMaterialUIController } from "app/providers";
import {
  collapseItem,
  collapseIconBox,
  collapseIcon,
  collapseText,
} from "./styles/sidenavCollapse";

function SidenavCollapse({
  icon,
  name,
  active = false,
  branchActive = false,
  collapse = null,
  route = null,
  href = null,
  level = 0,
  isMini = false,
  isMobile = false,
  onNavigate,
  onExpandMenu,
}) {
  const [controller] = useMaterialUIController();
  const { pathname } = useLocation();
  const { darkMode, sidenavColor } = controller;
  const transparentSidenav = !isMobile && controller.transparentSidenav;
  const whiteSidenav = !isMobile && controller.whiteSidenav;
  const [open, setOpen] = useState(branchActive);
  const submenuId = useId();
  useEffect(() => {
    if (branchActive) setOpen(true);
  }, [branchActive, pathname]);
  const hasChildren = Boolean(collapse);
  const handleClick = () => {
    if (hasChildren) {
      if (isMini) {
        onExpandMenu();
        setOpen(true);
      } else setOpen((value) => !value);
    } else onNavigate();
  };
  const linkProps =
    !hasChildren && route
      ? { component: NavLink, to: route, end: true }
      : !hasChildren && href
      ? { component: "a", href, target: "_blank", rel: "noopener noreferrer" }
      : { component: "button", type: "button" };
  return (
    <ListItem disablePadding sx={{ display: "block", mb: 0.5 }}>
      <Tooltip title={isMini ? name : ""} placement="right">
        <ListItemButton
          {...linkProps}
          onClick={handleClick}
          aria-label={name}
          aria-expanded={hasChildren ? open && !isMini : undefined}
          aria-controls={hasChildren ? submenuId : undefined}
          sx={(theme) => ({
            ...collapseItem(theme, {
              active: active || branchActive,
              transparentSidenav,
              whiteSidenav,
              darkMode,
              sidenavColor,
            }),
            pl: isMini ? 1 : 1.25 + level * 1.25,
            justifyContent: isMini ? "center" : "flex-start",
            minHeight: 44,
          })}
        >
          <ListItemIcon
            sx={(theme) =>
              collapseIconBox(theme, {
                transparentSidenav,
                whiteSidenav,
                darkMode,
                active: active || branchActive,
              })
            }
          >
            {typeof icon === "string" ? (
              <Icon sx={(theme) => collapseIcon(theme, { active: active || branchActive })}>
                {icon}
              </Icon>
            ) : (
              icon
            )}
          </ListItemIcon>
          {!isMini && (
            <ListItemText
              primary={name}
              sx={{ minWidth: 0, ml: 1 }}
              primaryTypographyProps={{
                noWrap: true,
                fontSize: "0.85rem",
                fontWeight: active || branchActive ? 600 : 400,
              }}
            />
          )}
          {hasChildren && !isMini && (
            <Icon
              sx={{
                ml: "auto",
                fontSize: "1.2rem",
                transform: open ? "rotate(180deg)" : "none",
                opacity: 0.7,
              }}
            >
              expand_more
            </Icon>
          )}
        </ListItemButton>
      </Tooltip>
      {hasChildren && (
        <Collapse in={open && !isMini} timeout="auto" unmountOnExit>
          <List
            component="ul"
            id={submenuId}
            disablePadding
            aria-label={`Opciones de ${name}`}
            sx={{ position: "relative" }}
          >
            {collapse}
          </List>
        </Collapse>
      )}
    </ListItem>
  );
}
SidenavCollapse.propTypes = {
  icon: PropTypes.node,
  name: PropTypes.string.isRequired,
  active: PropTypes.bool,
  branchActive: PropTypes.bool,
  collapse: PropTypes.node,
  route: PropTypes.string,
  href: PropTypes.string,
  level: PropTypes.number,
  isMini: PropTypes.bool,
  isMobile: PropTypes.bool,
  onNavigate: PropTypes.func.isRequired,
  onExpandMenu: PropTypes.func.isRequired,
};
export default SidenavCollapse;
export { collapseItem, collapseIconBox, collapseIcon, collapseText };
